import React, { useEffect } from 'react';
import { 
 CheckCircleIcon, 
 XCircleIcon, 
 ExclamationTriangleIcon, 
 InformationCircleIcon,
 XMarkIcon 
} from '@heroicons/react/24/outline';

/**
 * Toast Notification Component
 * 
 * Displays temporary notification messages for success, error, warning, and info states.
 * Auto-dismisses after a specified duration.
 */
const Toast = ({ 
 message, 
 type = 'info', 
 duration = 5000, 
 onClose,
 position = 'top-right'
}) => {
 useEffect(() => {
 if (duration > 0) {
 const timer = setTimeout(() => {
 onClose();
 }, duration);

 return () => clearTimeout(timer);
 }
 }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-emerald-600 shrink-0" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-rose-600 shrink-0" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0" />;
      case 'info':
      default:
        return <InformationCircleIcon className="w-5 h-5 text-primary-600 shrink-0" />;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return 'border-emerald-200/80';
      case 'error':
        return 'border-rose-200/80';
      case 'warning':
        return 'border-amber-200/80';
      case 'info':
      default:
        return 'border-slate-200';
    }
  };

  return (
    <div className="animate-slide-in-right" role="alert">
      <div className={`flex items-start gap-3 p-3.5 rounded-2xl bg-white/95 backdrop-blur-sm border ${getBorderColor()} shadow-lg shadow-slate-900/5 w-full transition-all`}>
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0 pr-1">
          <p className="text-xs font-semibold text-slate-800 leading-relaxed break-words">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-slate-400 hover:text-slate-600 p-0.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
          aria-label="Close notification"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
